import { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { captureProfileGuard } from "../../lib/profileSession";

type ListingPhotoUpload = { uploadType: string; listingId: string; file: File; label: string; error?: string };

/** [posting.photo-retry](../../../../docs/product/marketplace.md#posting-photo-retry):
 * retain failed originals only for this mounted profile/session; retries never repeat publication. */
export function useListingPhotoRetry() {
  const [uploadedCount, setUploadedCount] = useState(0);
  const [failedPhotos, setFailedPhotos] = useState<ListingPhotoUpload[]>([]);
  const [retryingPhotos, setRetryingPhotos] = useState(false);
  const session = useRef({ active: true, revision: 0, retrying: false, assertProfile: captureProfileGuard() });
  useEffect(() => {
    session.current.active = true;
    const leave = () => {
      session.current.active = false;
      session.current.revision++;
      session.current.retrying = false;
      setFailedPhotos([]);
      setRetryingPhotos(false);
    };
    // A browser-retained page may return without remounting; only new uploads may resume.
    const returnToPage = () => { session.current.active = true; };
    window.addEventListener("pagehide", leave);
    window.addEventListener("pageshow", returnToPage);
    return () => {
      session.current.active = false;
      session.current.revision++;
      window.removeEventListener("pagehide", leave);
      window.removeEventListener("pageshow", returnToPage);
    };
  }, []);
  function assertCurrent(revision: number) {
    session.current.assertProfile();
    if (!session.current.active || revision !== session.current.revision) throw new DOMException("Photo retry session ended.", "AbortError");
  }
  async function upload(photos: ListingPhotoUpload[], revision: number) {
    const failed: ListingPhotoUpload[] = [];
    for (const photo of photos) {
      try {
        assertCurrent(revision);
        await api.uploadPostImage(photo.uploadType, photo.listingId, photo.file);
        assertCurrent(revision);
        setUploadedCount(count => count + 1);
      } catch (failure) {
        failed.push({ ...photo, error: failure instanceof Error ? failure.message : "Photo upload failed." });
      }
    }
    return failed;
  }
  async function uploadPhotos(photos: ListingPhotoUpload[]) {
    const revision = session.current.revision;
    const failed = await upload(photos, revision);
    if (session.current.active && session.current.revision === revision) setFailedPhotos(previous => [...previous, ...failed]);
  }
  async function retryFailedPhotos() {
    if (!session.current.active || session.current.retrying || !failedPhotos.length) return;
    const revision = session.current.revision;
    session.current.retrying = true;
    setRetryingPhotos(true);
    try {
      const failed = await upload(failedPhotos, revision);
      if (session.current.active && session.current.revision === revision) setFailedPhotos(failed);
    } finally {
      if (session.current.revision === revision) { session.current.retrying = false; setRetryingPhotos(false); }
    }
  }
  return { uploadedCount, failedPhotos, retryingPhotos, uploadPhotos, retryFailedPhotos };
}
